/**
 * «Elektron yashil olam» — chap menyu (Mahsulotlar / Aksiyalar / Sevimlilar / Savat),
 * kategoriyalar, filtrlar va mehmon savati. Hammasi tizimga kirmasdan ishlaydi;
 * kirish faqat buyurtmani rasmiylashtirishda so'raladi (`useCheckout`).
 */
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@/app/AuthContext";
import { useI18n } from "@/shared/i18n";
import { formatMoney, formatNumber } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useFavorites, useGuestCart } from "@/shared/lib/shopStore";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { productPath } from "./market";
import type { PublicService } from "@/shared/api/clientArea";
import type { MarketItem } from "./market";

export type MarketSection = "products" | "promo" | "favorites" | "cart" | "services";

export function sectionPath(section: MarketSection) {
  return section === "products" ? SECTION_PATHS.market : `${SECTION_PATHS.market}?bolim=${section}`;
}

/* ------------------------------------------------------------------ xarid */

const CHECKOUT_TARGET = "/mijoz/bozor?checkout=1";

/**
 * Buyurtmani rasmiylashtirish: kirgan bo'lsa — mijoz kabinetiga, kirmagan bo'lsa — kirishga
 * (kirgandan keyin mehmon savati kabinetdagi savatga ko'chiriladi).
 */
export function useCheckout() {
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const loggedIn = status === "authenticated" && Boolean(user);
  return () =>
    navigate(loggedIn ? CHECKOUT_TARGET : `/kirish?next=${encodeURIComponent(CHECKOUT_TARGET)}&method=oneid`);
}

/* ------------------------------------------------------------------ menyu */

const MENU: Array<{ id: MarketSection; label: string; icon: IconName }> = [
  { id: "products", label: "Mahsulotlar", icon: "store" },
  { id: "promo", label: "Aksiyalar", icon: "tag" },
  { id: "favorites", label: "Sevimlilar", icon: "heart" },
  { id: "cart", label: "Savat", icon: "cart" },
];

export function MarketMenu({ active }: { active?: MarketSection }) {
  const favorites = useFavorites();
  const cart = useGuestCart();
  const counts: Partial<Record<MarketSection, number>> = { favorites: favorites.codes.length, cart: cart.count };
  return (
    <nav className="market-menu" aria-label="Elektron yashil olam">
      {MENU.map((entry) => (
        <Link
          key={entry.id}
          to={sectionPath(entry.id)}
          className={`market-menu-link${active === entry.id ? " is-on" : ""}`}
          aria-current={active === entry.id ? "page" : undefined}
        >
          <Icon name={entry.icon} size={18} />
          <span>{entry.label}</span>
          {counts[entry.id] ? <b className="market-menu-count">{counts[entry.id]}</b> : null}
        </Link>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------ kategoriyalar */

export interface Facet {
  code: string;
  name: string;
  count: number;
}

export function facetsOf(items: MarketItem[], pick: (item: MarketItem) => [string, string]): Facet[] {
  const map = new Map<string, Facet>();
  for (const item of items) {
    const [code, name] = pick(item);
    if (!code) continue;
    const row = map.get(code);
    if (row) row.count += 1;
    else map.set(code, { code, name: name || code, count: 1 });
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "uz"));
}

/** Kategoriyalar tartibi (qolganlari — oxirida); nomlar serverdagi toifalardan olinadi. */
const CATEGORY_ORDER = [
  "seedlings_ornamental",
  "seedlings_fruit",
  "shrubs",
  "flowers",
  "livestock",
  "timber",
  "forest_products",
  "seeds",
  "fertilizers",
  "fuel",
];

export function MarketCategories({
  items,
  category,
  servicesCount,
  servicesActive,
  onPick,
  onServices,
}: {
  items: MarketItem[];
  category: string;
  servicesCount: number;
  servicesActive: boolean;
  onPick: (category: string) => void;
  onServices: () => void;
}) {
  const facets = facetsOf(items, (item) => [item.categoryCode, item.categoryName]);
  const rank = (code: string) => {
    const index = CATEGORY_ORDER.indexOf(code);
    return index === -1 ? CATEGORY_ORDER.length : index;
  };
  const rows = [...facets].sort((a, b) => rank(a.code) - rank(b.code) || a.name.localeCompare(b.name, "uz"));
  return (
    <section className="market-panel" aria-labelledby="market-cats">
      <h2 id="market-cats">Kategoriyalar</h2>
      <ul className="market-cats">
        <li>
          <button type="button" className={!category && !servicesActive ? "is-on" : ""} onClick={() => onPick("")}>
            <span>Hammasi</span>
            <small>{items.length}</small>
          </button>
        </li>
        {rows.map((row) => (
          <li key={row.code}>
            <button type="button" className={category === row.code && !servicesActive ? "is-on" : ""} onClick={() => onPick(row.code)}>
              <span>{row.name}</span>
              <small>{row.count}</small>
            </button>
          </li>
        ))}
        <li>
          <button type="button" className={servicesActive ? "is-on" : ""} onClick={onServices}>
            <span>Xizmatlar</span>
            <small>{servicesCount}</small>
          </button>
        </li>
      </ul>
    </section>
  );
}

/* ----------------------------------------------------------------- xizmatlar */

/** «Xizmatlar» toifasi: ro'yxat kartochkalari; buyurtma berish — kabinetda (kirish talab etiladi). */
export function ServicesView({ services, query }: { services: PublicService[]; query: string }) {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const loggedIn = status === "authenticated" && Boolean(user);
  const needle = query.trim().toLocaleLowerCase(locale);
  const rows = services.filter((row) => !needle || `${row.name} ${row.description}`.toLocaleLowerCase(locale).includes(needle));
  const order = () => {
    const target = "/mijoz/bozor";
    navigate(loggedIn ? target : `/kirish?next=${encodeURIComponent(target)}&method=oneid`);
  };
  if (!rows.length) return <p className="market-empty">Xizmat topilmadi.</p>;
  return (
    <div className="market-grid">
      {rows.map((row) => (
        <article key={row.id} className="market-card">
          <div className="market-cover tone-3">
            <Icon name="briefcase" size={46} strokeWidth={1.3} />
            <span className="badge market-type">{row.category_name}</span>
          </div>
          <div className="market-body">
            <h3 title={row.name}>{row.name}</h3>
            <p className="market-price">
              {row.price > 0 ? (
                <>
                  <b className="num">{formatMoney(row.price, locale)}</b>
                  <span>/ {row.unit_name}</span>
                </>
              ) : (
                <b>Narx kelishiladi</b>
              )}
            </p>
            {row.description ? <p className="auc-card-purpose">{row.description}</p> : null}
            <div className="market-actions">
              <button type="button" className="btn btn-primary btn-sm market-buy" onClick={order}>
                Buyurtma berish
              </button>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ filtrlar */

export interface MarketFilterState {
  priceMin: string;
  priceMax: string;
  inStock: boolean;
  region: string;
  seller: string;
}

export const EMPTY_FILTERS: MarketFilterState = { priceMin: "", priceMax: "", inStock: false, region: "", seller: "" };

export function filtersActive(filters: MarketFilterState) {
  return Boolean(filters.priceMin || filters.priceMax || filters.inStock || filters.region || filters.seller);
}

export function applyFilters(item: MarketItem, filters: MarketFilterState) {
  const min = Number(filters.priceMin);
  const max = Number(filters.priceMax);
  return (
    (!filters.priceMin || item.price >= min) &&
    (!filters.priceMax || item.price <= max) &&
    (!filters.inStock || item.available > 0) &&
    (!filters.region || item.region === filters.region) &&
    (!filters.seller || item.seller === filters.seller)
  );
}

export function MarketFilters({
  items,
  value,
  onChange,
}: {
  items: MarketItem[];
  value: MarketFilterState;
  onChange: (next: MarketFilterState) => void;
}) {
  const regions = facetsOf(items, (item) => [item.region, item.region]);
  const sellers = facetsOf(items, (item) => [item.seller, item.seller]);
  const set = (patch: Partial<MarketFilterState>) => onChange({ ...value, ...patch });
  return (
    <section className="market-panel" aria-labelledby="market-filters">
      <h2 id="market-filters">Filtrlar</h2>
      <div className="market-filter">
        <span className="market-filter-label">Narx (so'm)</span>
        <div className="market-filter-range">
          <input
            className="input"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="dan"
            aria-label="Narx: dan"
            value={value.priceMin}
            onChange={(event) => set({ priceMin: event.target.value })}
          />
          <span aria-hidden="true">—</span>
          <input
            className="input"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="gacha"
            aria-label="Narx: gacha"
            value={value.priceMax}
            onChange={(event) => set({ priceMax: event.target.value })}
          />
        </div>
      </div>
      <label className="market-check">
        <input type="checkbox" checked={value.inStock} onChange={(event) => set({ inStock: event.target.checked })} />
        <span>Faqat mavjud mahsulotlar</span>
      </label>
      {regions.length > 1 ? (
        <label className="market-filter">
          <span className="market-filter-label">Hudud</span>
          <select className="input" value={value.region} onChange={(event) => set({ region: event.target.value })}>
            <option value="">Barchasi</option>
            {regions.map((row) => (
              <option key={row.code} value={row.code}>
                {row.name} ({row.count})
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {sellers.length > 1 ? (
        <label className="market-filter">
          <span className="market-filter-label">Sotuvchi</span>
          <select className="input" value={value.seller} onChange={(event) => set({ seller: event.target.value })}>
            <option value="">Barchasi</option>
            {sellers.map((row) => (
              <option key={row.code} value={row.code}>
                {row.name} ({row.count})
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {filtersActive(value) ? (
        <button type="button" className="btn btn-outline btn-sm btn-block" onClick={() => onChange(EMPTY_FILTERS)}>
          Filtrlarni tozalash
        </button>
      ) : null}
    </section>
  );
}

/* ---------------------------------------------------------------- miqdor */

/** Miqdor kiritish: − / son / +; `min`dan `max`gacha (max yo'q bo'lsa — cheklovsiz). */
export function QuantityInput({
  value,
  onChange,
  min = 1,
  max,
  label = "Miqdor",
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  label?: string;
}) {
  const clamp = (n: number) => {
    const safe = Number.isFinite(n) ? n : min;
    return Math.min(max ?? Infinity, Math.max(min, safe));
  };
  return (
    <div className="qty" role="group" aria-label={label}>
      <button type="button" className="qty-btn" aria-label="Kamaytirish" disabled={value <= min} onClick={() => onChange(clamp(value - 1))}>
        <Icon name="minus" size={16} />
      </button>
      <input
        className="qty-input"
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(clamp(Number(event.target.value)))}
      />
      <button
        type="button"
        className="qty-btn"
        aria-label="Ko'paytirish"
        disabled={max !== undefined && value >= max}
        onClick={() => onChange(clamp(value + 1))}
      >
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ savat */

export function CartView({ items }: { items: MarketItem[] }) {
  const { locale } = useI18n();
  const cart = useGuestCart();
  const checkout = useCheckout();
  const rows = cart.lines
    .map((line) => ({ line, item: items.find((entry) => entry.code === line.code) }))
    .filter((row): row is { line: typeof row.line; item: MarketItem } => Boolean(row.item));
  const total = rows.reduce((sum, row) => sum + row.item.price * row.line.quantity, 0);

  if (!rows.length) {
    return (
      <div className="market-empty-box">
        <Icon name="cart" size={40} strokeWidth={1.3} />
        <p>Savatingiz bo'sh.</p>
        <Link className="btn btn-outline" to={SECTION_PATHS.market}>
          Mahsulotlarga o'tish
        </Link>
      </div>
    );
  }
  return (
    <div className="market-cart">
      <ul className="market-cart-list">
        {rows.map(({ line, item }) => (
          <li key={item.code} className="market-cart-row">
            <Link to={productPath(item)} className="market-cart-pic">
              {item.image ? <img src={item.image} alt="" loading="lazy" /> : <Icon name="package" size={28} />}
            </Link>
            <div className="market-cart-main">
              <Link to={productPath(item)} className="market-cart-name">
                {item.name}
              </Link>
              <span className="market-cart-unit">
                {formatMoney(item.price, locale)} / {item.unit}
              </span>
              {item.available > 0 && item.available <= item.minOrder * 5 ? (
                <span className="market-stock is-low">Qoldi: {formatNumber(item.available, locale)} {item.unit}</span>
              ) : null}
            </div>
            <QuantityInput
              value={line.quantity}
              min={item.minOrder}
              max={item.available > 0 ? item.available : undefined}
              onChange={(next) => cart.set(item.code, next)}
            />
            <b className="num market-cart-sum">{formatMoney(item.price * line.quantity, locale)}</b>
            <button type="button" className="market-cart-del" aria-label="Savatdan olib tashlash" onClick={() => cart.remove(item.code)}>
              <Icon name="trash" size={18} />
            </button>
          </li>
        ))}
      </ul>
      <aside className="market-cart-total" aria-label="Buyurtma jami">
        <p>
          <span>Mahsulotlar</span>
          <b>{rows.length}</b>
        </p>
        <p className="is-total">
          <span>Jami</span>
          <b className="num">{formatMoney(total, locale)}</b>
        </p>
        <button type="button" className="btn btn-primary btn-block" onClick={checkout}>
          Buyurtmani rasmiylashtirish
        </button>
        <p className="market-cart-note">
          <Icon name="lock" size={14} />
          Rasmiylashtirish uchun tizimga kirasiz; savatingiz saqlanib qoladi.
        </p>
        <button type="button" className="btn btn-outline btn-sm btn-block" onClick={cart.clear}>
          Savatni tozalash
        </button>
      </aside>
    </div>
  );
}
